/**
 * Filter Panel Component
 * Displays a filterable panel with accordion-style sections for search filtering.
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettings } from 'store/app/appStore';
import type { FilterPanelApi } from '../../hooks/useFilterPanel';
import type { IFilterConfig } from '../../types/filter.types';
import { Accordion } from './Accordion';
import styles from './FilterPanel.module.css';

interface FilterPanelProps {
  filterApi: FilterPanelApi;
  filteredGamesCount: number | null;
  filters: IFilterConfig[];
  onClose: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({ filterApi, filteredGamesCount, filters, onClose }) => {
  const { t } = useTranslation();

  const { restrictions } = useSettings();
  const hasNoJackpotRestriction = restrictions.includes('noJackpot');

  const {
    openAccordionId,
    handleClearFilters,
    handleAccordionClick,
    handleFilterSelect,
    isFilterSelected,
    hasSelectedFilters,
    resetAccordion,
  } = filterApi;

  // Gets the appropriate label key for a filter based on settings restrictions
  const getFilterLabel = (filter: IFilterConfig) => {
    if (hasNoJackpotRestriction && filter.id === 'jackpot') {
      return t('search.filter_bonus', 'Bonus');
    }

    return t(filter.labelKey, filter.defaultLabel);
  };

  const handleClose = useCallback(() => {
    resetAccordion();
    onClose();
  }, [resetAccordion, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose],
  );

  return (
    <div className={styles.filterOverlayWrapper} onClick={handleOverlayClick}>
      <div className={styles.filterOverlay}>
        <div className={styles.filterHeader}>{t('search.filter_by', 'Filter By')}</div>
        <div className={styles.accordionsContainer}>
          {filters.map((filter) => (
            <Accordion
              key={filter.id}
              label={getFilterLabel(filter)}
              isOpen={openAccordionId === filter.id}
              onClick={() => handleAccordionClick(filter.id)}
              options={filter.options}
              onOptionSelect={(optionId) => handleFilterSelect(filter.id, optionId)}
              isOptionSelected={(optionId) => isFilterSelected(filter.id, optionId)}
              hasActiveFilters={hasSelectedFilters(filter.id)}
            />
          ))}
        </div>
        <div className={styles.filterActions}>
          <button className={styles.clearButton} onClick={handleClearFilters}>
            {t('search.clear_filters', 'Clear Filters')}
          </button>
          <button className={styles.showResultsButton} onClick={handleClose}>
            {t('search.show_results', 'Show Results')} {filteredGamesCount !== null ? `(${filteredGamesCount})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};
